import { useState, useEffect, useCallback, useMemo } from "react";
import {
  UseInsightsReturn,
  UseInsightGenerationReturn,
  UseInsightRefreshReturn,
  DashboardInsightsResponse,
  GenerateInsightRequest,
  GenerateInsightResponse,
  RefreshInsightResponse,
  SearchInsightsRequest,
  SearchInsightsResponse,
  DashboardLayout,
  DashboardPreferences,
  UpdateDashboardPreferencesRequest,
} from "../../../api/src/types/dashboardTypes";
import { Insight } from "../../../api/src/types/insights";
import supabaseClient from "../lib/supabaseClient";

// API client configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.sendupdates.ai";

// Helper function to get auth headers
const getAuthHeaders = async () => {
  try {
    const { data } = await supabaseClient.auth.getSession();
    if (data.session?.access_token) {
      return {
        Authorization: `Bearer ${data.session.access_token}`,
      };
    }
  } catch (error) {
    console.error("Error getting auth headers:", error);
  }
  return {};
};

// API request helper with authentication
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<{ success: boolean; data?: T; error?: string }> {
  try {
    const authHeaders = await getAuthHeaders();

    // Build headers object conditionally
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // Add Authorization header only if it exists
    if (authHeaders.Authorization) {
      headers.Authorization = authHeaders.Authorization;
    }

    // Add any additional headers from options
    if (options.headers) {
      Object.assign(headers, options.headers);
    }

    // 500s timeout for long-running LLM insight generation calls
    const controller = new AbortController();
    const fetchTimeout = setTimeout(() => controller.abort(), 500000);

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers,
      ...options,
      signal: controller.signal,
    });

    clearTimeout(fetchTimeout);

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    return { success: true, data: result.data || result };
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

// Cache management
const cache = new Map<string, { data: any; timestamp: number; ttl: number }>();

function getCached<T>(key: string): T | null {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < cached.ttl) {
    return cached.data;
  }
  cache.delete(key);
  return null;
}

function setCache<T>(key: string, data: T, ttl: number = 5 * 60 * 1000): void {
  cache.set(key, { data, timestamp: Date.now(), ttl });
}

// Local storage for preferences
function getStoredPreferences(
  userId: string
): Partial<DashboardPreferences> | null {
  try {
    const stored = localStorage.getItem(`insights_preferences_${userId}`);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function setStoredPreferences(
  userId: string,
  preferences: DashboardPreferences
): void {
  try {
    localStorage.setItem(
      `insights_preferences_${userId}`,
      JSON.stringify(preferences)
    );
  } catch {
    // Ignore storage errors
  }
}

/**
 * Hook for managing dashboard insights
 */
export function useInsights(userId: string): UseInsightsReturn {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [layout, setLayout] = useState<DashboardLayout[]>([]);
  const [preferences, setPreferences] = useState<DashboardPreferences | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Generate cache key
  const cacheKey = `insights_dashboard_${userId}`;

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    // Try cache first
    const cached = getCached<DashboardInsightsResponse>(cacheKey);
    if (cached) {
      setInsights(cached.insights);
      setLayout(cached.layout);
      setPreferences(cached.preferences);
      setIsLoading(false);
      return;
    }

    const result = await apiRequest<DashboardInsightsResponse>(
      "/insights/dashboard"
    );

    if (result.success && result.data) {
      setInsights(result.data.insights);
      setLayout(result.data.layout);
      setPreferences(result.data.preferences);

      // Cache the response
      setCache(cacheKey, result.data);

      // Store preferences locally
      setStoredPreferences(userId, result.data.preferences);
    } else {
      // Check if this is a database setup issue or missing endpoint (first-time user)
      const errorMessage = result.error || "Failed to load dashboard";
      const isDatabaseSetupError =
        (errorMessage.includes("relation") &&
          errorMessage.includes("does not exist")) ||
        (errorMessage.includes("table") &&
          errorMessage.includes("not found")) ||
        (errorMessage.includes("insights") &&
          errorMessage.includes("does not exist")) ||
        errorMessage.includes("HTTP 404") ||
        errorMessage.includes("Not Found") ||
        errorMessage.includes("Cannot GET");

      if (isDatabaseSetupError) {
        // Treat as empty state for first-time users
        console.log(
          "Database setup incomplete, showing empty state for first-time user experience"
        );
        setInsights([]);
        setLayout([]);
        setPreferences({
          userId: userId,
          layout: [],
          theme: "light",
          compactMode: false,
          showMetadata: true,
          autoRefresh: false,
          refreshInterval: 5, // 5 minutes (the interface expects minutes, not milliseconds)
        });
        setError(null); // Don't show error for database setup issues
      } else {
        setError(errorMessage);

        // Try to load from local storage as fallback
        const storedPrefs = getStoredPreferences(userId);
        if (storedPrefs) {
          setPreferences(storedPrefs as DashboardPreferences);
        }
      }
    }

    setIsLoading(false);
  }, [userId, cacheKey]);

  // Refresh dashboard
  const refresh = useCallback(async () => {
    cache.delete(cacheKey);
    await loadDashboard();
  }, [cacheKey, loadDashboard]);

  // Refresh specific insight
  const refreshInsight = useCallback(
    async (insightId: string) => {
      const result = await apiRequest<RefreshInsightResponse>(
        `/insights/${insightId}/refresh`,
        {
          method: "PUT",
        }
      );

      if (result.success && result.data) {
        setInsights((prev) =>
          prev.map((insight) =>
            insight.id === insightId ? result.data!.insight : insight
          )
        );

        // Invalidate cache
        cache.delete(cacheKey);
      } else {
        throw new Error(result.error || "Failed to refresh insight");
      }
    },
    [cacheKey]
  );

  // Remove insight
  const removeInsight = useCallback(
    async (insightId: string) => {
      const result = await apiRequest(`/insights/${insightId}`, {
        method: "DELETE",
      });

      if (result.success) {
        setInsights((prev) =>
          prev.filter((insight) => insight.id !== insightId)
        );
        setLayout((prev) => prev.filter((l) => l.insightId !== insightId));

        // Invalidate cache
        cache.delete(cacheKey);
      } else {
        throw new Error(result.error || "Failed to remove insight");
      }
    },
    [cacheKey]
  );

  // Update layout
  const updateLayout = useCallback(
    async (newLayout: DashboardLayout[]) => {
      // Optimistic update
      setLayout(newLayout);

      const result = await apiRequest("/insights/dashboard/preferences", {
        method: "POST",
        body: JSON.stringify({ layout: newLayout }),
      });

      if (!result.success) {
        // Revert on failure
        setError(result.error || "Failed to update layout");
        await loadDashboard();
      } else {
        // Invalidate cache
        cache.delete(cacheKey);
      }
    },
    [cacheKey, loadDashboard]
  );

  // Update preferences
  const updatePreferences = useCallback(
    async (updates: Partial<DashboardPreferences>) => {
      if (!preferences) return;

      const newPreferences = { ...preferences, ...updates };

      // Optimistic update
      setPreferences(newPreferences);
      setStoredPreferences(userId, newPreferences);

      const result = await apiRequest("/insights/dashboard/preferences", {
        method: "POST",
        body: JSON.stringify(updates),
      });

      if (!result.success) {
        // Revert on failure
        setError(result.error || "Failed to update preferences");
        await loadDashboard();
      } else {
        // Invalidate cache
        cache.delete(cacheKey);
      }
    },
    [preferences, userId, cacheKey, loadDashboard]
  );

  // Load dashboard on mount and user change
  useEffect(() => {
    if (userId) {
      loadDashboard();
    }
  }, [userId, loadDashboard]);

  // Auto-refresh based on preferences
  useEffect(() => {
    if (!preferences?.autoRefresh || !preferences.refreshInterval) return;

    const interval = setInterval(() => {
      refresh();
    }, preferences.refreshInterval * 60 * 1000);

    return () => clearInterval(interval);
  }, [preferences?.autoRefresh, preferences?.refreshInterval, refresh]);

  return {
    insights,
    layout,
    preferences: preferences || {
      userId,
      layout: [],
      autoRefresh: false,
      refreshInterval: 30,
      theme: "auto",
      compactMode: false,
      showMetadata: true,
    },
    isLoading,
    error,
    refresh,
    refreshInsight,
    removeInsight,
    updateLayout,
    updatePreferences,
  };
}

/**
 * Hook for generating new insights
 */
export function useInsightGeneration(): UseInsightGenerationReturn {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastGenerated, setLastGenerated] =
    useState<GenerateInsightResponse | null>(null);

  const generate = useCallback(
    async (
      request: GenerateInsightRequest
    ): Promise<GenerateInsightResponse> => {
      setIsGenerating(true);
      setError(null);

      try {
        const result = await apiRequest<GenerateInsightResponse>(
          "/insights/generate",
          {
            method: "POST",
            body: JSON.stringify(request),
          }
        );

        if (result.success && result.data) {
          setLastGenerated(result.data);
          return result.data;
        } else {
          throw new Error(result.error || "Failed to generate insight");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to generate insight";
        setError(errorMessage);
        throw err;
      } finally {
        setIsGenerating(false);
      }
    },
    []
  );

  const reset = useCallback(() => {
    setError(null);
    setLastGenerated(null);
  }, []);

  return {
    generate,
    isGenerating,
    error,
    lastGenerated,
    reset,
  };
}

/**
 * Hook for refreshing insights
 */
export function useInsightRefresh(): UseInsightRefreshReturn {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshingIds, setRefreshingIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const refreshInsight = useCallback(
    async (insightId: string): Promise<RefreshInsightResponse> => {
      setRefreshingIds((prev) => new Set(prev).add(insightId));
      setError(null);

      try {
        const result = await apiRequest<RefreshInsightResponse>(
          `/insights/${insightId}/refresh`,
          {
            method: "PUT",
          }
        );

        if (result.success && result.data) {
          return result.data;
        } else {
          throw new Error(result.error || "Failed to refresh insight");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to refresh insight";
        setError(errorMessage);
        throw err;
      } finally {
        setRefreshingIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(insightId);
          return newSet;
        });
      }
    },
    []
  );

  const refreshMultiple = useCallback(
    async (insightIds: string[]): Promise<RefreshInsightResponse[]> => {
      setIsRefreshing(true);
      setError(null);

      try {
        const result = await apiRequest<{ results: RefreshInsightResponse[] }>(
          "/insights/bulk/refresh",
          {
            method: "POST",
            body: JSON.stringify({ insightIds }),
          }
        );

        if (result.success && result.data) {
          return result.data.results;
        } else {
          throw new Error(result.error || "Failed to refresh insights");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to refresh insights";
        setError(errorMessage);
        throw err;
      } finally {
        setIsRefreshing(false);
      }
    },
    []
  );

  return {
    refreshInsight,
    refreshMultiple,
    isRefreshing,
    refreshingIds,
    error,
  };
}

/**
 * Hook for searching insights
 */
export function useInsightSearch() {
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] =
    useState<SearchInsightsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(
    async (params: SearchInsightsRequest): Promise<SearchInsightsResponse> => {
      setIsSearching(true);
      setError(null);

      try {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              queryParams.set(key, value.join(","));
            } else if (typeof value === "object") {
              if (key === "dateRange") {
                queryParams.set("startDate", value.start);
                queryParams.set("endDate", value.end);
              }
            } else {
              queryParams.set(key, String(value));
            }
          }
        });

        const result = await apiRequest<SearchInsightsResponse>(
          `/insights/search?${queryParams}`
        );

        if (result.success && result.data) {
          setSearchResults(result.data);
          return result.data;
        } else {
          throw new Error(result.error || "Failed to search insights");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to search insights";
        setError(errorMessage);
        throw err;
      } finally {
        setIsSearching(false);
      }
    },
    []
  );

  const reset = useCallback(() => {
    setSearchResults(null);
    setError(null);
  }, []);

  return {
    search,
    isSearching,
    searchResults,
    error,
    reset,
  };
}

/**
 * Hook for real-time insight updates
 */
export function useInsightUpdates(
  userId: string,
  onUpdate?: (insight: Insight) => void
) {
  useEffect(() => {
    // WebSocket connection for real-time updates
    // This would typically connect to a WebSocket endpoint
    const ws = new WebSocket(
      `${
        process.env.REACT_APP_WS_URL || "ws://localhost:8080"
      }/insights/${userId}`
    );

    ws.onmessage = (event) => {
      try {
        const update = JSON.parse(event.data);
        if (update.type === "insight_updated" && onUpdate) {
          onUpdate(update.insight);
        }
      } catch (error) {
        console.error("Failed to parse WebSocket message:", error);
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    return () => {
      ws.close();
    };
  }, [userId, onUpdate]);
}

/**
 * Hook for insight analytics and performance tracking
 */
export function useInsightAnalytics(insightId?: string) {
  const [analytics, setAnalytics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const trackView = useCallback(async (insightId: string) => {
    await apiRequest(`/insights/${insightId}/track`, {
      method: "POST",
      body: JSON.stringify({
        event: "view",
        timestamp: new Date().toISOString(),
      }),
    });
  }, []);

  const trackInteraction = useCallback(
    async (insightId: string, action: string, metadata?: any) => {
      await apiRequest(`/insights/${insightId}/track`, {
        method: "POST",
        body: JSON.stringify({
          event: "interaction",
          action,
          metadata,
          timestamp: new Date().toISOString(),
        }),
      });
    },
    []
  );

  const loadAnalytics = useCallback(async (insightId: string) => {
    setIsLoading(true);

    const result = await apiRequest(`/insights/${insightId}/analytics`);

    if (result.success && result.data) {
      setAnalytics(result.data);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (insightId) {
      loadAnalytics(insightId);
    }
  }, [insightId, loadAnalytics]);

  return {
    analytics,
    isLoading,
    trackView,
    trackInteraction,
    loadAnalytics,
  };
}
